/**
 * Backup Beacon, as drawn in R:\Projects\Backup Dashboard\brand.
 *
 * INLINE SVG RATHER THAN IMPORTED FILES. These are a few hundred bytes each,
 * they need to change with the theme, and the sign-in card renders before
 * anything else - an <img> there is a request that can be slow or fail on the
 * one screen where a missing logo looks like the wrong site. Inlining also
 * means the mark inherits nothing and cannot be broken by a stylesheet that
 * loads late.
 *
 * TWO VARIANTS, NOT ONE RECOLOURED. From the brand kit: "The arcs get lighter
 * as they move outward in light mode and darker as they move outward in dark
 * mode. Do not flip this - the gradient of weight is what makes it read as
 * emanation rather than as a decorative swoosh." That is why the dark files are
 * not simply the light ones with the fills swapped, and why this file carries
 * both rather than applying a filter.
 *
 * The wordmark keeps live text with a system fallback stack, which the kit says
 * is correct for the web app. Converting to outlines is for print handoff.
 */

import { useTheme } from './theme';

const NAVY = '#0B3A66';
const BLUE = '#2C7BD4';
const SKY = '#7FB4E8';
const MIST = '#DCEBFA';
const PAPER = '#E8F1FA';

const FONT = "Inter, 'Segoe UI', system-ui, -apple-system, sans-serif";

/** Mark only. 16px minimum, per the kit. */
export function Mark({ size = 40 }: { size?: number }) {
  const [theme] = useTheme();
  const dark = theme === 'dark';
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" role="img" aria-label="Backup Beacon">
      <circle cx="10" cy="20" r="4.5" fill={dark ? MIST : NAVY} />
      <path d="M18 11a11 11 0 0 1 0 18" fill="none" stroke={dark ? SKY : BLUE} strokeWidth="3.6" strokeLinecap="round" />
      <path d="M25.5 6a17.5 17.5 0 0 1 0 28" fill="none" stroke={dark ? BLUE : SKY} strokeWidth="3.6" strokeLinecap="round" />
    </svg>
  );
}

/** Mark plus wordmark. 120px wide minimum, per the kit. */
export function Wordmark({ width = 210 }: { width?: number }) {
  const [theme] = useTheme();
  const dark = theme === 'dark';
  return (
    <svg
      width={width} height={width * (56 / 270)} viewBox="0 0 270 56"
      role="img" aria-label="Backup Beacon"
    >
      <g transform="translate(4, 8)">
        <circle cx="10" cy="20" r="4.5" fill={dark ? MIST : NAVY} />
        <path d="M18 11a11 11 0 0 1 0 18" fill="none" stroke={dark ? SKY : BLUE} strokeWidth="3.6" strokeLinecap="round" />
        <path d="M25.5 6a17.5 17.5 0 0 1 0 28" fill="none" stroke={dark ? BLUE : SKY} strokeWidth="3.6" strokeLinecap="round" />
      </g>
      <text
        x="52" y="38" fontFamily={FONT} fontSize="30" letterSpacing="-0.6"
        fill={dark ? PAPER : NAVY}
      >
        {/* One weight break, never all caps - the wordmark rule from the kit. */}
        <tspan fontWeight="400">Backup</tspan><tspan fontWeight="600">Beacon</tspan>
      </text>
    </svg>
  );
}
